/**
 * Geometry calculation utilities for cross-section extraction
 */

import type { Point2D } from '../types';

/**
 * Utility class for geometric calculations in 2D space.
 * Provides methods for distance calculations, line projections, and bounding boxes.
 */
export class GeometryUtils {
  /**
   * Calculates Euclidean distance between two 2D points.
   *
   * @param p1 - First point [x, y]
   * @param p2 - Second point [x, y]
   * @returns Distance between the points
   */
  static distance2D(p1: Point2D, p2: Point2D): number {
    return Math.sqrt((p2[0] - p1[0]) ** 2 + (p2[1] - p1[1]) ** 2);
  }

  /**
   * Calculates perpendicular distance from a point to a line segment.
   * Also returns the parametric position and projected distance along the line.
   *
   * @param point - Point to measure distance from
   * @param lineStart - Start of line segment
   * @param lineEnd - End of line segment
   * @returns Tuple of [perpendicular distance, parametric parametricT (0-1), projected distance]
   *
   * @remarks
   * The parametric parametricT value indicates where along the line segment the closest point is:
   * - parametricValue = 0: closest point is at lineStart
   * - parametricValue = 1: closest point is at lineEnd
   * - 0 < parametricValue < 1: closest point is between start and end
   */
  static pointToLineDistance(
    point: Point2D,
    lineStart: Point2D,
    lineEnd: Point2D,
  ): [number, number] {
    const [x0, y0] = point;
    const [x1, y1] = lineStart;
    const [x2, y2] = lineEnd;

    const dx = x2 - x1;
    const dy = y2 - y1;

    const lengthSq = dx * dx + dy * dy;

    if (lengthSq === 0) {
      const distance = Math.sqrt((x0 - x1) ** 2 + (y0 - y1) ** 2);
      return [distance, distance];
    }

    // Calculate the parametric value for the closest point on the line segment.
    const parametricValue = Math.max(
      0,
      Math.min(1, ((x0 - x1) * dx + (y0 - y1) * dy) / lengthSq),
    );

    const projX = x1 + parametricValue * dx;
    const projY = y1 + parametricValue * dy;

    const distance = Math.sqrt((x0 - projX) ** 2 + (y0 - projY) ** 2);
    const projectedDistance = Math.sqrt((projX - x1) ** 2 + (projY - y1) ** 2);

    return [distance, projectedDistance];
  }

  /**
   * Calculates a tight bounding box around a line segment with buffer.
   * Creates a corridor perpendicular to the line segment.
   *
   * @param start - Start point of segment
   * @param end - End point of segment
   * @param buffer - Buffer distance from line (corridor half-width in each direction)
   * @returns Bounding box [minX, minY, maxX, maxY]
   *
   * @remarks
   * The bounding box is calculated by:
   * 1. Finding the perpendicular direction to the line segment
   * 2. Creating corners at ±buffer distance perpendicular to both endpoints
   * 3. Finding the min/max X and Y coordinates of all corners
   */
  static calculateTightBboxForSegment(
    start: Point2D,
    end: Point2D,
    buffer: number,
  ): [number, number, number, number] {
    const [x1, y1] = start;
    const [x2, y2] = end;

    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) {
      return [x1 - buffer, y1 - buffer, x1 + buffer, y1 + buffer];
    }

    const perpX = -dy / length;
    const perpY = dx / length;

    const corners: Point2D[] = [
      [x1 + perpX * buffer, y1 + perpY * buffer],
      [x1 - perpX * buffer, y1 - perpY * buffer],
      [x2 + perpX * buffer, y2 + perpY * buffer],
      [x2 - perpX * buffer, y2 - perpY * buffer],
    ];

    const xDataValues = corners.map((c) => c[0]);
    const yDataValues = corners.map((c) => c[1]);

    return [
      Math.min(...xDataValues),
      Math.min(...yDataValues),
      Math.max(...xDataValues),
      Math.max(...yDataValues),
    ];
  }

  /**
   * Calculates total length of a polyline path.
   *
   * @param path - Array of 2D points defining the path
   * @returns Total path length (sum of all segment distances)
   */
  static calculatePathLength(path: Point2D[]): number {
    return path.reduce((total, point, i) => {
      if (i === 0) return total;
      return total + GeometryUtils.distance2D(path[i - 1], point);
    }, 0);
  }
}
